import moment from 'moment'
import {
  EMAIL_NEWSLETTER_MAPPING,
  NewsletterSections,
  NewsTags,
  BrandIndustries,
} from '../../interfaces/newsfeed'
import { FilterFieldType, IFilter } from './types'

export const NEWS_FEED_LIST_FILTERS_CONFIG = [
  {
    field: 'headline',
    alias: 'newsfeedHeadlines',
    type: FilterFieldType.Text,
    isSortable: false,
    excludeColumn: true,
    title: 'Headline',
  },
  {
    field: 'headlineSummary',
    dbField: 'headline_summary',
    alias: 'newsfeedHeadlines',
    type: FilterFieldType.Text,
    isSortable: false,
    excludeColumn: true,
    title: 'Headline Summary',
  },
  {
    field: 'hasLinkedAccount',
    dbField: 'has_linked_account',
    type: FilterFieldType.List,
    isSortable: false,
    excludeFullSearchFilter: true, // Exclude from fullSearch to prevent errors
    title: 'Linked Account',
    options: [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' },
    ],
  },
  {
    field: 'brandIndustry',
    // No alias: dbField is a COALESCE expression so full-text search matches the
    // industry from either the company (industry_type) or organization
    // (industry_focus text[]) source — same coalesce buildBrandDto uses for display.
    dbField: `COALESCE(companyBrand.industry_type, array_to_string(organizationBrand.industry_focus, ','))`,
    type: FilterFieldType.List,
    isSortable: false,
    isMultiple: true,
    title: 'Brand Industry',
    options: Object.values(BrandIndustries).map((industry) => ({
      value: industry,
      label: industry,
    })),
  },
  {
    field: 'industry',
    dbField: 'industry',
    alias: 'newsfeedHeadlines',
    type: FilterFieldType.List,
    isSortable: false,
    isMultiple: true,
    excludeColumn: true,
    title: 'Industry',
    options: Object.values(BrandIndustries).map((industry) => ({
      value: industry,
      label: industry,
    })),
  },
  {
    field: 'type',
    alias: 'newsfeedHeadlines',
    type: FilterFieldType.List,
    placeholder: 'Type',
    title: 'Type',
    isMultiple: true,
    options: Object.values(NewsTags).map((tag) => ({
      value: tag,
      label: tag,
    })),
  },
  {
    field: 'newsletterSection',
    dbField: 'newsletter_section',
    alias: 'newsfeedHeadlines',
    type: FilterFieldType.List,
    placeholder: 'Section',
    title: 'Section',
    isMultiple: true,
    options: Object.values(NewsletterSections).map((section) => ({
      value: section,
      label: section,
    })),
  },
  {
    field: 'processedAt',
    dbField: 'processed_at',
    type: FilterFieldType.Date,
    placeholder: 'Date Range',
    title: 'Date Range',
    alias: 'newsfeedHeadlines',
  },
  {
    field: 'email',
    alias: 'newsfeedEmail',
    type: FilterFieldType.List,
    placeholder: 'Source',
    title: 'Source',
    isMultiple: true,
    options: Object.entries(EMAIL_NEWSLETTER_MAPPING).map(([key, value]) => ({
      value: key,
      label: value,
    })),
  },
  {
    // The autocomplete sends the brand NAME (loadOptions maps value→label), so we
    // match the headline's resolved brand text, not salesforce_id.
    field: 'brand',
    dbField: 'brand',
    alias: 'newsfeedHeadlines',
    type: FilterFieldType.Autocomplete,
    title: 'Brand',
  },
  {
    // Sends the linked-account NAME; the explicit filter is handled manually in
    // ListNewsfeed (no single name column on newsfeed_linked_accounts). dbField is a
    // COALESCE expression (no alias) only so full-text search still matches the
    // resolved name across all three CRM join aliases.
    field: 'referral_source',
    dbField: 'COALESCE(companyLinked.name, organizationLinked.name, entityInfoLinked.name)',
    type: FilterFieldType.Autocomplete,
    title: 'Other Accounts',
  },
  {
    field: 'sharedWithClients',
    placeholder: 'Shared with Clients',
    title: 'Shared with Clients',
    dbField: `CASE 
              WHEN clientNewsfeedHeadlines.id IS NOT NULL 
      AND clientNewsfeedHeadlines.display_until >= CURRENT_DATE
    THEN true 
              ELSE false 
              END`,
    filterTitle: 'Shared with Clients',
    type: FilterFieldType.Boolean,
    options: [
      {
        value: 'true',
        label: 'Yes',
        title: 'Shared with Clients',
      },
      {
        value: 'false',
        label: 'No',
        title: 'Not Shared with Clients',
      },
    ],
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Last 3 days',
        filters: {
          processedAtFrom: moment().subtract(3, 'days').format('YYYY-MM-DD'),
        },
      },
      {
        title: 'Shared with Clients',
        filters: {
          sharedWithClients: true,
        },
      },
      {
        title: 'Newsletter',
        filters: {
          newsletterSection: [
            NewsletterSections.RecentlyInDistributionNews,
            NewsletterSections.RecentlyInFundraiseNews,
          ],
        },
      },
    ],
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    title: 'Amount',
    excludeFilter: true,
  },
  {
    field: 'round',
    type: FilterFieldType.Text,
    title: 'Rmount',
    excludeFilter: true,
  },
] as const satisfies IFilter[]
